import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles, ArrowRight, CheckCircle2, Paintbrush, Layers, Home } from 'lucide-react'
import BeforeAfterSlider from './BeforeAfterSlider'

export default function BeforeAfter() {
  const [activeTab, setActiveTab] = useState('gulv')

  const examples = {
    gulv: {
      title: 'Professionel gulvbehandling',
      description: 'Se den fantastiske forvandling af trægulvet efter professionel slibning og efterfølgende lakering, der bringer gløden og slidstyrken tilbage.',
      before: 'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/1fec9621-01fc-4c1d-b2fb-740aa2ee5a1a.jpeg',
      after: 'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/dc061898-b483-4fda-9690-6e621c67b24b.jpeg',
      beforeLabel: 'Før',
      afterLabel: 'Efter'
    },
    terrasse: {
      title: 'Behandling af terrasse',
      description: 'Se den store forskel før og efter en professionel rensning og oliebehandling af en træterrasse.',
      before: 'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/64410012-dcaa-40af-a58c-df5091bd0c59.png',
      after: 'https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/123da160-92ed-420f-b60b-5a174ae8a5f3.png',
      beforeLabel: 'Før',
      afterLabel: 'Efter'
    }
  }

  const currentExample = examples[activeTab]

  return (
    <section className="py-20 bg-gray-50/50 border-y border-gray-100 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#01529E]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#ED1C26]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Text & Info */}
          <div className="lg:col-span-5 space-y-6">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3.5 py-2 rounded-full">
                <Sparkles className="h-3.5 w-3.5" />
                Før / Efter Resultater
              </span>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-4 leading-tight">
                Se vores fantastiske før/efter resultater
              </h2>
              <p className="text-[#01529E] font-semibold text-sm sm:text-base mt-2">
                Professionelt malerarbejde, gulv- og terrassebehandling i Østjylland
              </p>
              <p className="text-gray-600 mt-4 text-sm sm:text-base leading-relaxed">
                Hos Malerfirma Villy Poulsen ApS leverer vi altid et perfekt resultat med sans for detaljen. Træk i skyderen på eksemplerne for at se den store forskel, vores professionelle arbejde gør.
              </p>
            </div>

            {/* Feature list */}
            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-gray-900">Professionel gulvbehandling</h4>
                  <p className="text-xs text-gray-500">Slibning og lakering, der vækker træet til live.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-gray-900">Indendørs malerarbejde</h4>
                  <p className="text-xs text-gray-500">Professionel spartling og maling af vægge og lofter.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-bold text-gray-900">Terrassebehandling</h4>
                  <p className="text-xs text-gray-500">Dybdegående rensning og oliebehandling af træterrasser.</p>
                </div>
              </div>
            </div>

            {/* Quick Switch Tabs */}
            <div className="pt-4 border-t border-gray-100">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-3">
                Vælg eksempel:
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveTab('gulv')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                    activeTab === 'gulv'
                      ? 'bg-[#01529E] text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Paintbrush className="h-3.5 w-3.5" />
                  Gulvbehandling
                </button>
                <button
                  onClick={() => setActiveTab('terrasse')}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                    activeTab === 'terrasse'
                      ? 'bg-[#01529E] text-white shadow-md'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Terrasse
                </button>
              </div>
            </div>

            {/* Button to Gallery */}
            <div className="pt-4">
              <Link
                to="/galleri"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#01529E] to-[#014285] hover:from-[#ED1C26] hover:to-[#d1141d] text-white font-bold text-sm rounded-xl transition-all duration-300 shadow-md hover:shadow-lg cursor-pointer"
              >
                Se hele vores billedgalleri
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* Right Column: Live Interactive Slider */}
          <div className="lg:col-span-7 h-full flex flex-col justify-center">
            <div className="relative">
              {/* Decorative frame shadow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#01529E]/10 to-[#ED1C26]/10 rounded-[32px] blur-xl opacity-70 pointer-events-none" />
              
              <div className="relative">
                <BeforeAfterSlider
                  key={activeTab}
                  title={currentExample.title}
                  description={currentExample.description}
                  before={currentExample.before}
                  after={currentExample.after}
                  beforeLabel={currentExample.beforeLabel}
                  afterLabel={currentExample.afterLabel}
                  heightClass="h-[350px] sm:h-[450px]"
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  )
}
